import { defineStore } from 'pinia';
import {useMockData,mockProduct} from "/public/mock-data/products";
import axios from 'axios';

export const useAppStore = defineStore('app', {
    state: () => ({
        // Main product data
        mainProduct: {},
        mainProductID: null,
        full_product: null,
        full_product_json: null,
        responseProduct: {},
        
        // Application type and product type
        applicationType: 'default',
        productType: null,
        
        // Store identification
        storeID: null,
        isComda: false,
        
        // Product flags
        isCustomBundlePage: false,
        isEnvelopProduct: false,
        
        // Customer data
        isCustomerGuest: true,
        b2bCompanyId: null,    // numeric string company ID for the current B2B customer, or null
        b2bTierPrices: null,   // parsed metafield JSON or null
        
        // Group ID for cart operations
        currentGroupID: null,
        
        // API configuration
        apiBaseUrl: "",
        currentLocationUrl: "",
        
        // Loading states
        disableButtonFirstStep: true,

        // Inventory / stock status ('In Stock', 'Out of Stock', etc.)
        inventoryStatus: null,
        
        // Calendar specific data
        calendar: {
            productList: []
        },
        
        // Calendar single page data (for data-attributes)
        calendarSingleData: {
            productId: null,
            productTitle: '',
            productImage: '',
            quantity: 25,
            unitPrice: 0,  // Base unit price for calendar product
            tierPrice: []  // Tier pricing matrix for bundle calculation
        },
        
        // Associated IDs (for color product mapping)
        associatedIds: null
    }),

    getters: {
        // Get main full main product
        getMainProduct(state) {
            return state.mainProduct;
        },
        
        // Get MainProductId grouped
        getMainProductId(state) {
            return state.mainProduct?.id || state.mainProductID;
        },

        getApplicationType(state) {
            // Dynamic application type based on runtime detection
            if (this.isCalendarSingle) {
                return 'calendar_single';
            }
            if (state.applicationType === 'calendar') {
                return 'calendar';
            }
            return 'default';
        },
        
        // Check if it's custom bundle page
        getIsCustomBundlePage(state) {
            return state.isCustomBundlePage;
        },
        
        // Check if product is envelope
        getIsEnvelopProduct(state) {
            return state.isEnvelopProduct;
        },
        
        // Get current group ID
        getCurrentGroupID(state) {
            return state.currentGroupID;
        },
        
        // Get color product ID by variant ID
        getColorProductIdByVariantId: (state) => (variantId) => {
            if (!state.associatedIds) {
                console.error('[AppStore] associatedIds not loaded');
                return variantId;
            }
            
            const idColor = state.associatedIds.simples?.id_color;
            if (Array.isArray(idColor)) {
                // If it's an array, use the last element
                return idColor[idColor.length - 1];
            }
            return idColor || variantId;
        },
        
        /**
         * Check if current mode requires Color & Quantity step
         * For calendar modes - hide Color & Quantity
         */
        shouldShowColorQuantityStep(state) {
            return state.applicationType === 'default';
        },
        
        /**
         * Get minimum order quantity based on application type
         */
        getMinOrderQty(state) {
            if (state.applicationType === 'calendar' || 
                state.applicationType === 'calendar_single') {
                return state.isComda ? 100 : 150;
            }
            // Return from product data for default mode
            return state.full_product_json?.min_order_qty || 25;
        },
        
        /**
         * Check if current page is calendar single (bundle entry point)
         * Simplified detection using applicationType and DOM checks
         */
        isCalendarSingle(state) {
            // Must be on a product page
            const isProductPage = window.location.pathname.includes('/products/');
            
            // Check if bundle tag exists
            const bundleTag = document.getElementById('it-is-bundle-product');
            const isBundleProduct = bundleTag?.textContent === 'yes';
            
            // Check if it's a calendar product (multiple ways)
            const isCalendar = 
                state.applicationType === 'calendar_single' ||  // Already detected
                state.full_product?.is_calendar === 'true' ||   // From full_product JSON
                state.full_product?.is_calendar === true ||     // Boolean variant
                state.full_product?.tags?.includes('calendar') || // Shopify tags
                state.full_product?.product_type === 'Calendar'; // Product type
            
            // All conditions must be true
            const result = isProductPage && isCalendar && isBundleProduct;
            
            // DEBUG: Always log to see what's happening
            console.log('[AppStore] isCalendarSingle check:', {
                isProductPage,
                isCalendar,
                isBundleProduct,
                result,
                pathname: window.location.pathname,
                applicationType: state.applicationType,
                fullProduct: state.full_product,
                fullProductIsCalendar: state.full_product?.is_calendar,
                fullProductTags: state.full_product?.tags,
                fullProductType: state.full_product?.product_type,
                bundleTagElement: bundleTag,
                bundleTagText: bundleTag?.textContent
            });
            
            return result;
        },
        
        /**
         * Check if calendar mode is active
         */
        isCalendarMode(state) {
            return state.applicationType === 'calendar' || 
                   state.applicationType === 'calendar_single';
        },

        isOutOfStock(state) {
            return state.inventoryStatus === 'Out of Stock';
        },
        
        /**
         * Get calendar products with computed total quantity
         */
        getCalendarProducts(state) {
            return state.calendar.productList.map(product => ({
                ...product,
                totalQty: product.qty || 0
            }));
        },

        /**
         * Return B2B tiers for the given imprint SKU.
         *
         * Security model:
         *   - #b2b_tier_prices and #b2b_company_id are only injected by Liquid
         *     for customer.b2b? visitors — non-B2B users never populate these.
         *   - b2bCompanyId = customer.current_company.id (injected by Liquid).
         *     We look up tiers ONLY under that exact key in the metafield, so a
         *     wrong/mismatched company ID in the metafield does NOT show B2B prices.
         *   - The backend independently validates via Admin API (resolveB2BCompanyId).
         */
        getB2BTierForSku: (state) => (imprintSku) => {
            if (!state.b2bTierPrices) return null;
            if (!state.b2bCompanyId) return null;

            const tiers = state.b2bTierPrices[state.b2bCompanyId]?.[imprintSku];
            if (tiers?.length > 0) return tiers;

            console.warn(
                `[AppStore] B2B — no tiers found for companyId=${state.b2bCompanyId} sku=${imprintSku}. ` +
                `Available keys: [${Object.keys(state.b2bTierPrices).join(', ')}]`
            );
            return null;
        },
    },

    actions: {
        /**
         * Set associated IDs (called from ColorSizeSection after loading)
         */
        setAssociatedIds(ids) {
            this.associatedIds = ids;
            console.log('[AppStore] Associated IDs set:', this.associatedIds);
        },

        /**

         * Initialize main product ID based on page context
         * This replaces the created() logic from StepNavigation
         */
        async initMainProductID() {
            let response = null;

            // Handle mock data for development
            if (useMockData()) {
                this.mainProductID = mockProduct.id;
                return this.mainProductID;
            }

            // Check if it's NOT a calendar bundle custom page
            if (window.location.pathname !== "/collections/calendar-bundle-custom") {
                const element = document.getElementById("current_main_product_id");
                if (element) {
                    response = element.textContent;
                    this.mainProductID = parseInt(response);
                    console.log("Main product ID initialized:", this.mainProductID);
                }
            }

            // Handle calendar_single application type
            if (this.applicationType === "calendar_single") {
                const mainProductElement = window.jQuery(".main_product_id")[0];
                if (mainProductElement) {
                    response = mainProductElement.textContent;
                    this.mainProductID = parseInt(response);
                    
                    const fullProductElement = window.jQuery("#full_product_info_json--wall");
                    if (fullProductElement.length) {
                        this.full_product = JSON.parse(fullProductElement.text());
                    }
                }
            }

            // Handle calendar bundle custom page
            if (window.location.pathname === "/collections/calendar-bundle-custom" && 
                this.applicationType !== "calendar_single") {
                console.log("Calendar Bundle page detected");
                this.applicationType = 'calendar';
                
                const mainProductElement = window.jQuery(".main_product_id")[0];
                if (mainProductElement) {
                    response = mainProductElement.textContent;
                    this.mainProductID = parseInt(response);
                    
                    const fullProductJsonElement = window.jQuery(`.full_product_info_json-${this.mainProductID}`)[0];
                    if (fullProductJsonElement) {
                        this.full_product = JSON.parse(fullProductJsonElement.textContent);
                    }
                }
            }

            console.log("mainProductID initialized:", this.mainProductID);

            // Get full product info JSON
            const fullProductInfoElement = document.getElementById("full_product_info_json");
            if (fullProductInfoElement) {
                this.full_product_json = JSON.parse(fullProductInfoElement.textContent);
            }

            // Check if it's a calendar envelope product
            if (this.isCalendarEnvelopProduct()) {
                this.isEnvelopProduct = true;
            }

            return this.mainProductID;
        },

        /**
         * Check if product is a calendar envelope
         */
        isCalendarEnvelopProduct() {
            return window.jQuery("#is_calendar_envelop_product").length > 0;
        },

        /**
         * Set inventory status (ported from StepNavigstion.vue setInventoryStatus()).
         * Drives isOutOfStock getter, which blocks Add to Cart.
         * Must be called AFTER full_product_json is loaded (i.e. after initMainProductID()).
         */
        setInventoryStatus() {
            console.log('-----Set Inventory Status ----- ');
            this.inventoryStatus = 'In Stock';

            // Set inventory for application type default and calendar
            if (this.full_product_json?.is_in_stock !== undefined
                && this.applicationType !== 'calendar_single'
            ) {
                this.inventoryStatus = this.full_product_json.is_in_stock.trim();
                console.log('set status from full_product_json' + this.inventoryStatus);
            }

            // Set inventory for wall calendars
            if (this.applicationType === 'calendar_single') {
                const statusEl = document.getElementById("inventory_status");

                if (statusEl) {
                    const status = statusEl.textContent.trim();
                    if (status) {
                        this.inventoryStatus = status;
                        console.log('inventory status for simple calendar ' + this.inventoryStatus);
                    } else {
                        console.log('inventory status is empty for calendar_sku');
                        console.log(status);
                    }
                } else {
                    console.log('inventory_status element not found for calendar_sku');
                }
            }
        },

        /**
         * Set application type based on page context
         */
        async initApplicationType() {
            // Check for single calendar bundle
            const singleCalendarBundleElement = document.getElementById("it-is-bundle-product");
            if (singleCalendarBundleElement) {
                const singleCalendarBundleTag = singleCalendarBundleElement.textContent;
                console.log("Single calendar bundle tag:", singleCalendarBundleTag);
                
                if (singleCalendarBundleTag === 'yes') {
                    this.applicationType = 'calendar_single';
                }
            }

            // Check for custom bundle page
            const customBundlePageElement = document.getElementById("it-is-bundle-custom-page");
            if (customBundlePageElement) {
                const customBundlePage = customBundlePageElement.textContent;
                if (customBundlePage === 'yes') {
                    this.isCustomBundlePage = true;
                    console.log("Custom bundle page detected");
                }
            }

            console.log("Application type initialized:", this.applicationType);
        },

        /**
         * Initialize store ID based on theme settings
         */
        initStoreID() {
            if (window.PXUTheme?.theme_settings?.project_store_name && window.Shopify?.country) {
                this.storeID = `${window.PXUTheme.theme_settings.project_store_name}_${window.Shopify.country.toLowerCase()}`;
                console.log("Store ID initialized:", this.storeID);
            }
        },

        /**
         * Initialize if current site is Comda
         */
        initIsComda() {
            this.isComda = window.location.href.includes('comda');
            this.currentLocationUrl = window.location.host;
            console.log("Is Comda:", this.isComda);
        },

        /**
         * Check if customer is guest (not logged in) and load B2B data.
         *
         * B2B data is read from DOM elements injected by json_info__block.liquid.
         * Both elements are only present when customer.b2b? is true in Liquid,
         * so non-B2B visitors never populate b2bCompanyId or b2bTierPrices.
         *
         * b2bCompanyId (#b2b_company_id) — customer.current_company.id from Liquid.
         *   Used to match against metafield keys before showing B2B prices.
         * b2bTierPrices (#b2b_tier_prices) — custom.b2b_tier_prices metafield JSON.
         */
        initIsCustomerGuest() {
            const customerIdElement = document.getElementById("customer_id");
            if (customerIdElement) {
                const customerId = customerIdElement.textContent;
                this.isCustomerGuest = !customerId;
            }
            console.log("Is customer guest:", this.isCustomerGuest);

            // Read B2B company ID from DOM (only present for customer.b2b? visitors).
            const b2bCompanyIdEl = document.getElementById("b2b_company_id");
            const rawCompanyId = b2bCompanyIdEl?.textContent?.trim();
            this.b2bCompanyId = rawCompanyId || null;

            // Read B2B tier prices from DOM (only present for customer.b2b? visitors).
            const b2bTierPricesEl = document.getElementById("b2b_tier_prices");
            const rawTiers = b2bTierPricesEl?.textContent?.trim();
            if (rawTiers) {
                try {
                    this.b2bTierPrices = JSON.parse(rawTiers);
                } catch (e) {
                    console.error('[AppStore] Failed to parse b2b_tier_prices JSON:', e);
                    this.b2bTierPrices = null;
                }
            }

            console.log('[AppStore] B2B — companyId:', this.b2bCompanyId, '| hasTierPrices:', !!this.b2bTierPrices);
        },

        /**
         * Generate random string for group ID
         */
        generateRandomString(length) {
            const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
            const charactersLength = characters.length;
            let result = '';

            // Create an array of 32-bit unsigned integers
            const randomValues = new Uint32Array(length);

            // Generate random values
            window.crypto.getRandomValues(randomValues);
            randomValues.forEach((value) => {
                result += characters.charAt(value % charactersLength);
            });

            return result;
        },

        /**
         * Generate unique group ID for cart grouping
         */
        generateGroupID() {
            const date = new Date();
            const timestamp = date.getTime();
            const randomString = this.generateRandomString(30);
            
            this.currentGroupID = `${timestamp}_${randomString}`;
            return this.currentGroupID;
        },

        /**
         * Initialize API base URL from theme settings
         */
        initApiBaseUrl() {
            if (window.PXUTheme?.theme_settings?.api_beckend_url) {
                this.apiBaseUrl = window.PXUTheme.theme_settings.api_beckend_url;
                console.log("API base URL initialized:", this.apiBaseUrl);
            } else {
                // Fallback to dev URL
                this.apiBaseUrl = "https://dev.api-customapp.comda.com/";
                console.error("Please set theme setting: api_beckend_url");
            }
        },

        /**
         * Get product by Shopify handle
         */
        async getProductByHandle(handle) {
            try {
                console.log("Fetching product by handle:", handle);
                const response = await axios.get(`${window.Shopify.routes.root}products/${handle}.js`);
                
                console.log("Product fetched:", response.data);
                console.log("Product type:", response.data.type);
                
                this.setProductType(response.data.type);
                this.disableButtonFirstStep = false;
                
                return response.data;
            } catch (error) {
                console.error("Error fetching product by handle:", error);
                throw error;
            }
        },

        /**
         * Set product type (grouped_plain, etc.)
         */
        setProductType(type) {
            this.productType = type;
            console.log("Product type set:", type);
        },

        /**
         * Get main product handle by ID using search API
         */
        async getMainProductHandleById(id) {

            if(useMockData()){
                const mainProduct = JSON.parse(mockProduct.mainProduct)
                this.setProductType(mainProduct.type);
                return true;
            }
            try {
                const response = await axios.get(
                    `/search/suggest.json?q=id:${id}&resources[type]=product&limit=1`
                );
                
                this.responseProduct = response?.data || null;
                
                if (this.responseProduct?.resources?.results?.products?.[0]) {
                    const handle = this.responseProduct.resources.results.products[0].handle;
                    await this.getProductByHandle(handle);
                }
            } catch (error) {
                console.error("Error fetching product handle by ID:", error);
                throw error;
            }
        },

        /**
         * Initialize products from calendar storage (localStorage)
         */
        initProductsFromCalendarStorage() {
            console.log("Initializing calendar products from storage");
            
            if (localStorage.fullStorageProductList && localStorage.fullStorageProductList.length) {
                try {
                    this.calendar.productList = JSON.parse(localStorage.fullStorageProductList);
                    console.log("Calendar products loaded:", this.calendar.productList);
                } catch (error) {
                    console.error("Error parsing calendar products from storage:", error);
                }
            }
        },
        
        /**
         * Update calendar product quantity
         * Called from UI when user changes qty on calendar bundle page
         */
        updateCalendarProductQty(productId, newQty) {
            const product = this.calendar.productList.find(p => p.id === productId);
            if (product) {
                product.qty = newQty;
                // Sync back to localStorage
                this.saveCalendarProductsToStorage();
                console.log('[AppStore] Calendar product qty updated:', productId, newQty);
            }
        },
        
        /**
         * Save calendar products back to localStorage
         */
        saveCalendarProductsToStorage() {
            if (this.applicationType === 'calendar' || this.applicationType === 'calendar_single') {
                localStorage.setItem(
                    'fullStorageProductList', 
                    JSON.stringify(this.calendar.productList)
                );
                console.log('[AppStore] Calendar products saved to localStorage');
            }
        },
        
        /**
         * Clear calendar products
         */
        clearCalendarProducts() {
            this.calendar.productList = [];
            localStorage.removeItem('fullStorageProductList');
            localStorage.removeItem('storageProductList');
            console.log('[AppStore] Calendar products cleared');
        },
        
        /**
         * Load calendar single page data from DOM
         * This data is used for data-attributes on "Add to Bundle" button
         * Called from CalendarSinglePage component on mount
         */
        loadCalendarSingleData() {
            if (!this.isCalendarSingle) {
                console.warn('[AppStore] Not a calendar single page, skipping data load');
                return;
            }
            
            console.log('[AppStore] Loading calendar single data from DOM...');
            
            // Get product ID from DOM element
            const idElement = document.getElementById('simple_calendar_pr_id');
            if (idElement) {
                this.calendarSingleData.productId = parseInt(idElement.textContent);
                console.log('[AppStore] Product ID loaded:', this.calendarSingleData.productId);
            } else {
                console.error('[AppStore] Element #simple_calendar_pr_id not found!');
            }
            
            // Get product title (without jQuery)
            const titleElement = document.querySelector('h1.product_name');
            if (titleElement) {
                this.calendarSingleData.productTitle = titleElement.textContent.trim();
                console.log('[AppStore] Product title loaded:', this.calendarSingleData.productTitle);
            } else {
                console.error('[AppStore] Element h1.product_name not found!');
            }
            
            // Get product image - try two sources
            const galleryCell = document.querySelector('.gallery-cell');
            if (galleryCell) {
                // First try: lightbox link
                const lightbox = galleryCell.querySelector('a.lightbox');
                if (lightbox) {
                    this.calendarSingleData.productImage = lightbox.getAttribute('href');
                    console.log('[AppStore] Product image loaded from lightbox:', this.calendarSingleData.productImage);
                } else {
                    // Second try: img data-src
                    const img = galleryCell.querySelector('img.product-gallery__image');
                    if (img) {
                        this.calendarSingleData.productImage = img.getAttribute('data-src') || img.getAttribute('src');
                        console.log('[AppStore] Product image loaded from img:', this.calendarSingleData.productImage);
                    } else {
                        console.error('[AppStore] Image not found in gallery-cell!');
                    }
                }
            } else {
                console.error('[AppStore] Element .gallery-cell not found!');
            }
            
            // Get product price from DOM or Shopify Product
            // Try multiple sources for reliability
            let unitPrice = 0;
            
            // Source 1: Try Shopify Product JSON (most reliable)
            if (window.Shopify?.Product?.variants && window.Shopify.Product.variants.length > 0) {
                // Get price from first variant (calendars usually have one variant)
                const variant = window.Shopify.Product.variants[0];
                if (variant && variant.price) {
                    // Shopify price is in cents, divide by 100 to get dollars
                    unitPrice = parseFloat(variant.price) / 100;
                    console.log('[AppStore] Unit price loaded from Shopify.Product.variants:', unitPrice);
                }
            }
            
            // Source 2: Fallback to DOM element with class .price
            if (unitPrice === 0) {
                const priceElement = document.querySelector('.price .money, .price, [data-product-price]');
                if (priceElement) {
                    // Extract price from text (remove $, commas, etc.)
                    const priceText = priceElement.textContent.trim().replace(/[$,]/g, '');
                    const parsedPrice = parseFloat(priceText);
                    
                    if (!isNaN(parsedPrice)) {
                        unitPrice = parsedPrice;
                        console.log('[AppStore] Unit price loaded from DOM .price:', unitPrice);
                    }
                }
            }
            
            // Source 3: Try meta tag (product:price:amount)
            if (unitPrice === 0) {
                const metaPrice = document.querySelector('meta[property="product:price:amount"]');
                if (metaPrice) {
                    const price = parseFloat(metaPrice.getAttribute('content'));
                    if (!isNaN(price)) {
                        unitPrice = price;
                        console.log('[AppStore] Unit price loaded from meta tag:', unitPrice);
                    }
                }
            }
            
            // Store the price
            this.calendarSingleData.unitPrice = unitPrice;
            
            if (unitPrice === 0) {
                console.warn('[AppStore] Could not find product price! Calendar will have $0.00 unit price.');
            }
            
            // Get tier pricing matrix for calendar bundle pricing
            let tierPrice = [];
            
            // Try to get tier_price from Shopify Product
            if (window.Shopify?.Product?.variants && window.Shopify.Product.variants.length > 0) {
                const variant = window.Shopify.Product.variants[0];
                if (variant && variant.tier_price) {
                    tierPrice = variant.tier_price;
                    console.log('[AppStore] Tier price loaded from Shopify.Product.variants:', tierPrice);
                }
            }
            
            // Fallback: try to get from full_product JSON if available
            if (tierPrice.length === 0 && this.full_product?.prices_2?.tier_price) {
                tierPrice = this.full_product.prices_2.tier_price;
                console.log('[AppStore] Tier price loaded from full_product:', tierPrice);
            }
            
            // Store tier price matrix
            this.calendarSingleData.tierPrice = tierPrice;
            
            if (tierPrice.length === 0) {
                console.warn('[AppStore] No tier price matrix found! Will use fixed unitPrice.');
            }
            
            console.log('[AppStore] Calendar single data loaded:', this.calendarSingleData);
        },

        /**
         * Main initialization method - replaces created() hook logic
         * Call this from component's setup() or onMounted()
         */
        async initialize() {
            console.log("=== Starting App Store Initialization ===");
            
            try {
                // Initialize basic settings
                this.initStoreID();
                this.initIsComda();
                this.initIsCustomerGuest();
                this.initApiBaseUrl();
                
                // Generate initial group ID
                this.generateGroupID();
                
                // Initialize application type and check for bundle pages
                await this.initApplicationType();
                
                // Load calendar single data early (before component mounts)
                // This ensures data is available when CalendarBundle app initializes
                if (this.applicationType === 'calendar_single') {
                    this.loadCalendarSingleData();
                }
                
                // Initialize main product ID
                await this.initMainProductID();

                // Set inventory status (needs full_product_json loaded by initMainProductID())
                this.setInventoryStatus();

                // If we have a main product ID, fetch its details
                if (this.mainProductID) {
                    await this.getMainProductHandleById(this.mainProductID);
                }
                
                // Initialize calendar products if needed
                if (this.applicationType === 'calendar') {
                    this.initProductsFromCalendarStorage();
                }
                
                console.log("=== App Store Initialization Complete ===");
                console.log("Store state:", {
                    mainProductID: this.mainProductID,
                    applicationType: this.applicationType,
                    isCustomBundlePage: this.isCustomBundlePage,
                    isEnvelopProduct: this.isEnvelopProduct,
                    productType: this.productType,
                    storeID: this.storeID,
                    isComda: this.isComda,
                    associatedIds: this.associatedIds
                });
                
            } catch (error) {
                console.error("Error during app store initialization:", error);
                throw error;
            }
        }
    }
});
